"use client";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { CategoryNode } from "@/types/pim";
import { useUpdateFilters } from "../hooks/useUpdateFilters";
import { useCatalogParams } from "../hooks/useCatalogParams";
import { ConfigProvider, theme } from "antd";
import MobileFilterMenu from "./MobileFilterMenu";

import Sidebar from "./InventoryList.Sidebar";
import Subcategories from "./InventoryList.Subcategories";
import LocationFilter from "./InventoryList.LocationFilter";
import TagFilters from "./InventoryList.TagFilters";
import type { TagFilter } from "@/types/tag-filters";
import { useInventoryNav } from "./InventoryNavContext";
import { InventoryFiltersProvider, type InventoryFiltersData } from "./InventoryFiltersContext";
import { CatalogResultsProvider, useCatalogResults } from "./InventoryList.ResultsContext";
import SearchBar from "./InventoryHome.SearchBar";

/**
 * InventoryList is now the catalog SHELL: toolbars, subcategory row, Location
 * and tag filter rows, and the attribute sidebar — everything that doesn't need
 * the item query. The item grid streams in through `children` (a Suspense
 * boundary rendering CatalogGrid), so the chrome is visible and interactive
 * while the items are still loading. Result counts flow back from the grid via
 * CatalogResultsContext.
 */
interface TInventoryListProps {
  selectedCategory?: CategoryNode | null;
  searchQuery?: string;
  filterData?: InventoryFiltersData;
  /** Tag-filter registry (scoped to the current category), resolved on the server. */
  tagFilters?: TagFilter[];
  /** The streamed item grid (Suspense boundary around CatalogGrid). */
  children: React.ReactNode;
}

export const InventoryList: React.FC<TInventoryListProps> = ({
  selectedCategory,
  searchQuery,
  filterData = { attributes: [], attributeValues: {}, availableValues: {}, subcategoryCounts: null },
  tagFilters = [],
  children,
}) => {
  const router = useRouter();
  const pathname = usePathname();

  const { updateFilter } = useUpdateFilters();
  // Shared navigation transition — isNavigating is true while a list-changing
  // navigation (filter/search/category) is in flight.
  const { isNavigating, runNavigation } = useInventoryNav();

  const scrollParentRef = useRef<HTMLDivElement>(null);

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterClosing, setIsFilterClosing] = useState(false);
  const [isFilterOpening, setIsFilterOpening] = useState(false);

  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const { attributeFilters } = useCatalogParams();

  // Handle opening the mobile filter with animation
  const openMobileFilter = () => {
    setIsMobileFilterOpen(true);
    setIsFilterOpening(true);
    // Trigger animation after DOM update
    setTimeout(() => {
      setIsFilterOpening(false);
    }, 50);
  };

  // Handle closing the mobile filter with animation
  const closeMobileFilter = () => {
    setIsFilterClosing(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterClosing(false);
    }, 300); // Match the animation duration
  };

  // Prevent body scroll when mobile filter is open (iOS Safari compatible)
  useEffect(() => {
    if (isMobileFilterOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;

      // Apply styles to prevent scrolling on iOS Safari
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // Return cleanup function
      return () => {
        // Remove the fixed positioning
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileFilterOpen]);

  // Handle attribute filter changes
  const handleAttributeFilterChange = (attributeName: string, value: any) => {
    runNavigation(() => updateFilter(attributeName, value));
  };

  // Handle search
  const searchParams = useSearchParams();
  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    runNavigation(() => router.push(`${pathname}?${params.toString()}`));
  };

  // Clear all filters
  const clearAllFilters = () => {
    const preservedParams = new URLSearchParams();
    if (searchParams.get("search")) {
      preservedParams.set("search", searchParams.get("search")!);
    }
    // Preserve the Location filter — clearing should not drop the selected location(s).
    searchParams.getAll("Location").forEach((value) => preservedParams.append("Location", value));
    // Preserve active tags too — "Clear filters" clears attribute facets, not the tag selection.
    searchParams.getAll("tags").forEach((value) => preservedParams.append("tags", value));

    const newPathname = preservedParams.toString() ? `${pathname}?${preservedParams.toString()}` : pathname;

    runNavigation(() => router.push(newPathname));

    if (isMobileFilterOpen) {
      setIsMobileFilterOpen(false);
    }
  };

  // Active filter count — only the clearable attribute filters. Location, search,
  // and category are excluded since the clear buttons intentionally preserve them.
  const activeFilterCount = useMemo(() => {
    return Object.keys(attributeFilters).filter((key) => key !== "Location").length;
  }, [attributeFilters]);

  return (
    <InventoryFiltersProvider value={filterData}>
    <CatalogResultsProvider>
    <div className="flex flex-col flex-1">

      {/* Mobile toolbar */}
      <div
        className="sticky top-0 z-60 flex items-center gap-3 justify-end px-2 py-2 lg:hidden bg-gslate shadow-sm"
        style={{
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
        }}
      >
        <ConfigProvider
          theme={{
            algorithm: theme.darkAlgorithm,
            token: {
              colorBgContainer: '#3a3a3a',
              colorBorder: '#5a5a5a',
              colorText: '#e5e5e5',
              colorTextPlaceholder: '#9ca3af',
              fontSize: 16,
            },
          }}
        >
          <SearchBar />

          <button
            ref={filterButtonRef}
            id="filter"
            className="flex items-center gap-2 px-4 py-1.25 border rounded-lg text-sm font-medium transition-colors relative"
            style={{
              backgroundColor: '#3a3a3a',
              borderColor: '#5a5a5a',
              color: '#e5e5e5',
            }}
            onClick={() => isMobileFilterOpen ? closeMobileFilter() : openMobileFilter()}
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-gcopper text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </ConfigProvider>
      </div>

      {/* Subcategory, Location, and Tag toolbars share one loading state: grayed out
          and dimmed like disabled controls, with a soft blur pulse — no spinner, no
          layout shift — so they update in unison while a navigation is in flight.
          Grayscale/opacity sit on the outer wrapper and the blur pulse on the inner
          one so the two filter effects stack instead of overriding each other. */}
      <div
        className={`transition duration-200 ${isNavigating ? 'pointer-events-none opacity-60 grayscale' : ''}`}
        aria-busy={isNavigating}
      >
        <div className={isNavigating ? 'animate-blur-pulse' : ''}>
          {selectedCategory?.children && selectedCategory.children.length > 0 && (
            <Subcategories selectedCategory={selectedCategory} />
          )}

          {/* Full-width Location filter buttons, mirroring the sidebar's Location filter */}
          {selectedCategory && (
            <LocationFilter
              category={selectedCategory}
              filters={attributeFilters}
              searchQuery={searchQuery}
              onFilterChange={handleAttributeFilterChange}
            />
          )}

          {/* Tag filters below the Location row: a button row (e.g. "New Items")
              followed by any full-width promotional banners (e.g. a closeout sale) */}
          {tagFilters.length > 0 && (
            <TagFilters tagFilters={tagFilters} />
          )}
        </div>
      </div>

      <MobileResultsStrip />

      <div className="flex flex-1 bg-transparent max-w-full" style={{ flexDirection: selectedCategory ? 'row' : 'column' }}>

        <Sidebar
          activeFilterCount={activeFilterCount}
          clearAllFilters={clearAllFilters}
          selectedCategory={selectedCategory}
          attributeFilters={attributeFilters}
          handleAttributeFilterChange={handleAttributeFilterChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
        />

        <div className="relative flex flex-col flex-1 pt-4 bg-transparent" ref={scrollParentRef} aria-busy={isNavigating}>
          {/* Loading feedback while the list is being recomputed (filters/search/category change).
              Absolutely positioned over the grid, biased to the top, so it doesn't shift the cards. */}
          {isNavigating && (
            <div className="pointer-events-none absolute inset-x-0 top-6 z-20 flex justify-center">
              <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm ring-1 ring-gray-200">
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gcopper"
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-gray-700">Updating results…</span>
              </div>
            </div>
          )}
          {selectedCategory && (
            <h1 className="sr-only">
              {selectedCategory.Name}
            </h1>
          )}
          <div className={`flex flex-col gap-4 transition-opacity duration-200 ${isNavigating ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
            {children}
          </div>
          <div className="w-full h-56 bg-transparent"></div>
        </div>
      </div>

      {/* Mobile filter overlay */}
      <MobileFilterMenu
        isOpen={isMobileFilterOpen}
        isFilterOpening={isFilterOpening}
        isFilterClosing={isFilterClosing}
        closeMobileFilter={closeMobileFilter}
        clearAllFilters={clearAllFilters}
        activeFilterCount={activeFilterCount}
        selectedCategory={selectedCategory || null}
        attributeFilters={attributeFilters}
        onFilterChange={handleAttributeFilterChange}
      />

    </div>
    </CatalogResultsProvider>
    </InventoryFiltersProvider>
  );
};

/**
 * Mobile-only "Showing X of Y items" strip. Reads counts from the results
 * context (published by the streamed grid) so it can live in the shell; hidden
 * until the grid has mounted and published.
 */
const MobileResultsStrip: React.FC = () => {
  const { loadedCount, total } = useCatalogResults();
  // Height is reserved even before the grid publishes so the counts filling in
  // after hydration don't shift the layout (unlike the sidebar, mobile has no
  // other spacer around this row).
  return (
    <div className="flex justify-end items-center w-full min-h-7 pt-2 pr-5 text-sm text-gray-600 wrap-break-word lg:hidden">
      {loadedCount !== null && total !== null ? (
        <>Showing {loadedCount.toLocaleString()} of {total.toLocaleString()} items</>
      ) : null}
    </div>
  );
};

export default InventoryList;
